const TYPE_LABELS = {
  reels: "Reels",
  stories: "Stories",
  anuncio: "Anúncio",
  anúncio: "Anúncio",
  paid: "Anúncio",
  video_curto: "Reels",
} as const;

const OBJETIVO_LABELS = {
  educativo: "Educativo",
  engajamento: "Engajamento",
  conversao: "Conversão",
  conversão: "Conversão",
  autoridade: "Autoridade",
} as const;

export interface ParsedRoteiroSlide {
  slide?: number;
  content: string;
  sceneDirection?: string | null;
  durationLabel?: string | null;
  durationSeconds?: number | null;
  cta?: string | null;
  highlight?: string | null;
}

export interface ParsedRoteiroPayload {
  raw: string;
  typeLabel?: string | null;
  objetivoLabel?: string | null;
  typeValue?: "Reels" | "Stories" | "Anúncio" | null;
  objetivoValue?: "educativo" | "engajamento" | "conversão" | "autoridade" | null;
  hook?: string;
  desenvolvimento?: string;
  cta?: string;
  duracaoEstimadaSegundos?: number | null;
  hashtags: string[];
  estruturaTexto?: string | null;
  estruturaSlides?: ParsedRoteiroSlide[];
  plainSections?: ParsedRoteiroSection[];
}

function getBlockLabel(typeValue: ParsedRoteiroPayload["typeValue"], index: number): string {
  if (typeValue === "Stories") return `Story ${index + 1}`;
  return `Slide ${index + 1}`;
}

export interface ParsedRoteiroSection {
  label: string;
  body: string;
}

function stripCodeFences(raw: string): string {
  return raw.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
}

/**
 * Best-effort JSON repair for LLM outputs that sometimes emit a stray
 * trailing brace or comma. Tries the raw text first; if that fails, walks
 * back from the last `}` or `]` and retries until a parse succeeds.
 * Defensive only — never throws past the caller; if nothing parses, the
 * caller still has the string fallback.
 */
function parseJsonCandidate(raw: string): unknown {
  const cleaned = stripCodeFences(raw);
  try {
    return JSON.parse(cleaned);
  } catch {
    // Walk back from the END to the previous closing brace/bracket. Stop after
    // a handful of attempts so we don't pathologically truncate.
    let attempts = 0;
    let end = cleaned.length - 1;
    while (attempts < 8 && end > 0) {
      while (end > 0 && cleaned[end] !== "}" && cleaned[end] !== "]") end -= 1;
      if (end <= 0) break;
      try {
        return JSON.parse(cleaned.slice(0, end + 1));
      } catch {
        end -= 1;
        attempts += 1;
      }
    }
    // Walk forward from the start, dropping leading noise.
    const firstBrace = cleaned.indexOf("{");
    if (firstBrace > 0) {
      try {
        return JSON.parse(cleaned.slice(firstBrace));
      } catch { /* ignore */ }
    }
    throw new Error("parseJsonCandidate: unrepairable");
  }
}

function unwrapCandidate(input: unknown): unknown {
  let current = input;

  for (let i = 0; i < 4; i += 1) {
    if (typeof current === "string") {
      try {
        current = parseJsonCandidate(current);
        continue;
      } catch {
        return current;
      }
    }

    if (!current || typeof current !== "object") return current;

    const record = current as Record<string, unknown>;
    if (typeof record.script === "string") {
      current = record.script;
      continue;
    }
    if (typeof record.analysis === "string" || (record.analysis && typeof record.analysis === "object")) {
      current = record.analysis;
      continue;
    }
    return record;
  }

  return current;
}

export function normalizeTypeLabel(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase() as keyof typeof TYPE_LABELS;
  return TYPE_LABELS[key] ?? value;
}

export function normalizeObjetivoLabel(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase() as keyof typeof OBJETIVO_LABELS;
  return OBJETIVO_LABELS[key] ?? value;
}

export function normalizeTypeValue(value: unknown): "Reels" | "Stories" | "Anúncio" | null {
  const label = normalizeTypeLabel(value);
  if (label === "Reels" || label === "Stories" || label === "Anúncio") return label;
  return null;
}

export function normalizeObjetivoValue(
  value: unknown,
): "educativo" | "engajamento" | "conversão" | "autoridade" | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  if (key === "educativo") return "educativo";
  if (key === "engajamento") return "engajamento";
  if (key === "autoridade") return "autoridade";
  if (key === "conversão" || key === "conversao") return "conversão";
  return null;
}

function normalizeHashtags(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function parseDurationSeconds(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const match = value.match(/(\d+(?:[.,]\d+)?)/);
  if (!match) return null;
  const normalized = Number(match[1].replace(",", "."));
  return Number.isFinite(normalized) ? normalized : null;
}

function getFirstString(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return "";
}

function normalizeSlides(value: unknown): ParsedRoteiroSlide[] {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([key]) => /^(?:slide|story)[_\s-]?\d+$/i.test(key))
      .sort(([a], [b]) => {
        const numA = Number(a.match(/\d+/)?.[0] ?? 0);
        const numB = Number(b.match(/\d+/)?.[0] ?? 0);
        return numA - numB;
      });

    return entries
      .map(([key, entry], index) => {
        if (typeof entry === "string" && entry.trim().length > 0) {
          return {
            slide: Number(key.match(/\d+/)?.[0] ?? index + 1),
            content: entry.trim(),
            durationLabel: null,
            durationSeconds: null,
            cta: null,
            highlight: null,
          } satisfies ParsedRoteiroSlide;
        }

        if (!entry || typeof entry !== "object") return null;
        const record = entry as Record<string, unknown>;
        const composedBlocks = [
          getFirstString(record, ["hook"]),
          getFirstString(record, ["story", "content", "texto", "text", "body", "copy"]),
          getFirstString(record, ["offer", "oferta"]),
        ].filter(Boolean);
        const content = composedBlocks.join("\n\n");
        if (!content) return null;

        return {
          slide: Number(key.match(/\d+/)?.[0] ?? index + 1),
          content,
          durationLabel: getFirstString(record, ["duration", "duracao", "duração", "tempo"]) || null,
          durationSeconds:
            parseDurationSeconds(record.duration) ??
            parseDurationSeconds(record.duracao) ??
            parseDurationSeconds(record["duração"]) ??
            parseDurationSeconds(record.tempo),
          cta: getFirstString(record, ["cta", "call_to_action", "acao", "ação"]) || null,
          highlight:
            getFirstString(record, ["destaque", "highlight", "ênfase", "enfase"]) || null,
        } satisfies ParsedRoteiroSlide;
      })
      .filter((item): item is ParsedRoteiroSlide => Boolean(item));
  }

  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => {
      if (typeof item === "string" && item.trim().length > 0) {
        return {
          slide: index + 1,
          content: item.trim(),
          durationLabel: null,
          durationSeconds: null,
          cta: null,
          highlight: null,
        } satisfies ParsedRoteiroSlide;
      }

      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      // `fala` = spoken dialogue (v4 prompt); fallback to legacy content fields
      const content = getFirstString(record, [
        "fala",
        "voiceover",
        "vo",
        "dialogo",
        "diálogo",
        "content",
        "texto",
        "text",
        "copy",
        "body",
        "script",
        "roteiro",
        "legenda",
      ]);
      if (!content) return null;
      // `cena` = scene direction (v4 prompt)
      const sceneDirection =
        getFirstString(record, ["cena", "scene", "direction", "direcao", "direção", "visual", "take"]) || null;
      const durationLabel = getFirstString(record, ["duration", "duracao", "duração", "tempo"]) || null;
      const cta = getFirstString(record, ["cta", "call_to_action", "acao", "ação"]) || null;
      const highlight = getFirstString(record, ["destaque", "highlight", "ênfase", "enfase"]) || null;
      return {
        slide: typeof record.slide === "number" ? record.slide : index + 1,
        content,
        sceneDirection,
        durationLabel,
        durationSeconds:
          parseDurationSeconds(record.duration) ??
          parseDurationSeconds(record.duracao) ??
          parseDurationSeconds(record["duração"]) ??
          parseDurationSeconds(record.tempo),
        cta,
        highlight,
      } satisfies ParsedRoteiroSlide;
    })
    .filter((item): item is ParsedRoteiroSlide => Boolean(item));
}

function prettifySectionLabel(label: string): string {
  return label
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const CANONICAL_KEYS = new Set([
  "tipo",
  "type",
  "tipo_roteiro",
  "objetivo",
  "objective",
  "objetivo_roteiro",
  "estrutura_gerada",
  "estrutura",
  "duracao_estimada_segundos",
  "duracao_estimada_s",
  "duracao",
  "hashtags_sugeridas",
  "hashtags",
  "hook",
  "desenvolvimento",
  "cta",
  "roteiro",
  "copy",
  "script",
]);

const TIMED_KEY_REGEX = /^(hook|promise|payoff|desenvolvimento|cta|abordagem|foco|results?|estrutura)[_\s-]*\w*[_\s-]*\d*[sx]?$/i;
const HASHTAG_REGEX = /#[\wÀ-ſ]+/g;

/**
 * Defense-in-depth salvage for non-canonical roteiro JSON shapes.
 *
 * When neither `estrutura_gerada` nor free-text fields are present, prompts may
 * have emitted bespoke schemas (e.g. `hook_0_3s`, `promise_4_15s`,
 * `abordagem_c1`). Walk every string-valued top-level field, turn it into a
 * plain section, and order timed segments by their leading numeric tag so the
 * UI never falls back to raw JSON.
 */
function salvageRecordSections(
  record: Record<string, unknown>,
): { sections: ParsedRoteiroSection[]; hashtags: string[] } {
  const timed: { order: number; section: ParsedRoteiroSection }[] = [];
  const other: ParsedRoteiroSection[] = [];
  const hashtagSet = new Set<string>();

  for (const [key, value] of Object.entries(record)) {
    if (CANONICAL_KEYS.has(key.toLowerCase())) continue;
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed) continue;

    for (const match of trimmed.match(HASHTAG_REGEX) ?? []) {
      hashtagSet.add(match);
    }

    const label = prettifySectionLabel(key);
    const section: ParsedRoteiroSection = { label, body: trimmed };

    if (TIMED_KEY_REGEX.test(key)) {
      const numMatch = key.match(/\d+/);
      const order = numMatch ? Number(numMatch[0]) : 0;
      timed.push({ order, section });
    } else {
      other.push(section);
    }
  }

  timed.sort((a, b) => a.order - b.order);
  return {
    sections: [...timed.map((t) => t.section), ...other],
    hashtags: [...hashtagSet],
  };
}

function extractPlainSections(text: string, defaultLabel = "Roteiro"): ParsedRoteiroSection[] {
  // Collapse only horizontal whitespace (spaces/tabs), preserve newlines
  const normalized = text.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  if (!normalized) return [];

  const markerRegex = /([^\S\r\n]*[🔎🎥🧠💬⚡🎬📌🔥✨]?\s*\[[^\]]+\])/gu;
  const matches = Array.from(normalized.matchAll(markerRegex));

  if (matches.length === 0) {
    return [{ label: defaultLabel, body: normalized }];
  }

  const sections = matches.map((match, index) => {
    const marker = match[0].trim();
    const start = (match.index ?? 0) + match[0].length;
    const end = index + 1 < matches.length ? (matches[index + 1].index ?? normalized.length) : normalized.length;
    const rawLabel = marker.replace(/[🔎🎥🧠💬⚡🎬📌🔥✨]/gu, "").trim();
    const body = normalized.slice(start, end).trim().replace(/^[—:-]\s*/, "");
    return {
      label: prettifySectionLabel(rawLabel.replace(/^\[|\]$/g, "")),
      body,
    };
  });

  return sections.filter((section) => section.body.length > 0);
}

export function normalizeTopicForFamily(topic: string | null | undefined): string {
  if (!topic) return "";
  return topic
    .trim()
    .replace(/^v\d+\s+/i, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function buildRoteiroReferenceText(script: string): string {
  const parsed = parseRoteiroPayload(script);
  if (!parsed) return script.trim().slice(0, 900);

  if (parsed.estruturaSlides?.length) {
    return parsed.estruturaSlides
      .map((slide, index) => `${getBlockLabel(parsed.typeValue, index)}: ${slide.content}`)
      .join("\n")
      .slice(0, 1200);
  }

  if (parsed.plainSections?.length) {
    return parsed.plainSections
      .map((section) => `${section.label}: ${section.body}`)
      .join("\n")
      .slice(0, 1200);
  }

  return [parsed.hook, parsed.desenvolvimento, parsed.cta, parsed.estruturaTexto]
    .filter(Boolean)
    .join("\n")
    .slice(0, 1200);
}

export function parseRoteiroPayload(script: string): ParsedRoteiroPayload | null {
  if (!script.trim()) return null;

  const unwrapped = unwrapCandidate(script);
  if (typeof unwrapped === "string") {
    return {
      raw: script,
      hashtags: [],
      estruturaTexto: unwrapped,
    };
  }

  if (!unwrapped || typeof unwrapped !== "object") return null;

  const record = unwrapped as Record<string, unknown>;
  const estrutura = record.estrutura_gerada;
  const slides = normalizeSlides(estrutura);
  const estruturaTexto =
    typeof estrutura === "string" && estrutura.trim().length > 0 ? estrutura.trim() : null;
  const freeText =
    estruturaTexto ??
    (typeof record.roteiro === "string" ? record.roteiro.trim() : null) ??
    (typeof record.copy === "string" ? record.copy.trim() : null);
  const typeValue =
    normalizeTypeValue(record.tipo) ??
    normalizeTypeValue(record.type) ??
    normalizeTypeValue(record.tipo_roteiro);
  const objetivoValue =
    normalizeObjetivoValue(record.objetivo) ??
    normalizeObjetivoValue(record.objective) ??
    normalizeObjetivoValue(record.objetivo_roteiro);
  let plainSections = freeText
    ? extractPlainSections(freeText, typeValue === "Stories" ? "Story 1" : "Roteiro")
    : [];

  let hashtags = normalizeHashtags(record.hashtags_sugeridas);

  // Salvage path: when neither slides nor free-text exist, the prompt emitted a
  // bespoke schema (e.g. `hook_0_3s`, `abordagem_c1`). Surface every string
  // field as a section so the UI never renders raw JSON.
  if (slides.length === 0 && plainSections.length === 0) {
    const salvaged = salvageRecordSections(record);
    if (salvaged.sections.length > 0) {
      plainSections = salvaged.sections;
    }
    if (hashtags.length === 0 && salvaged.hashtags.length > 0) {
      hashtags = salvaged.hashtags;
    }
  }

  return {
    raw: script,
    typeLabel:
      normalizeTypeLabel(record.tipo) ??
      normalizeTypeLabel(record.type) ??
      normalizeTypeLabel(record.tipo_roteiro),
    objetivoLabel:
      normalizeObjetivoLabel(record.objetivo) ??
      normalizeObjetivoLabel(record.objective) ??
      normalizeObjetivoLabel(record.objetivo_roteiro),
    typeValue,
    objetivoValue,
    hook: typeof record.hook === "string" ? record.hook.trim() : undefined,
    desenvolvimento:
      typeof record.desenvolvimento === "string" ? record.desenvolvimento.trim() : undefined,
    cta: typeof record.cta === "string" ? record.cta.trim() : undefined,
    duracaoEstimadaSegundos:
      parseDurationSeconds(record.duracao_estimada_segundos) ??
      parseDurationSeconds(record.duracao_estimada_s),
    hashtags,
    estruturaTexto: freeText,
    estruturaSlides: slides.length > 0 ? slides : undefined,
    plainSections: plainSections.length > 0 ? plainSections : undefined,
  };
}
